window.VAE = window.VAE || {};

VAE.HybridTest = (function () {
  'use strict';

  // Test the hybrid approach
  async function runHybridTest() {
    console.log('=== VAE Hybrid Test: axe-core + LLM ===');
    
    try {
      // Step 1: Load and run axe-core
      console.log('Step 1: Loading axe-core...');
      await VAE.AxeIntegration.loadAxeCore();
      console.log('✓ axe-core loaded successfully');

      // Step 2: Run axe analysis
      console.log('Step 2: Running axe-core analysis...');
      const axeAnalysis = await VAE.AxeIntegration.runAxeAnalysis();
      console.log(`✓ axe-core found ${axeAnalysis.violations.length} violations`);

      // Step 3: Transform axe results
      console.log('Step 3: Transforming axe results...');
      const transformedAxeResults = VAE.AxeIntegration.transformAxeResults(axeAnalysis);
      console.log(`✓ Transformed ${transformedAxeResults.length} axe issues`);

      // Step 4: Display axe results
      console.log('Step 4: Displaying axe results...');
      displayAxeResults(transformedAxeResults);

      // Step 4.5: VAE ID Coverage Statistics
      const vaeIdStats = calculateVaeIdCoverage(transformedAxeResults);
      console.log('📊 VAE ID Coverage:', vaeIdStats);

      // Step 4.6: Auto-Fixability Statistics
      const autoFixStats = calculateAutoFixabilityStats(transformedAxeResults);
      console.log('🔧 Auto-Fixability:', autoFixStats);

      // Step 4.7: Consistency Check
      console.log('Step 4.7: Running consistency check...');
      const consistencyReport = await VAE.AxeIntegration.runConsistencyCheck(3);
      console.log('🔍 Consistency Report:', consistencyReport);

      // Step 5: Test API integration (if configured)
      if (VAE.Config?.client?.token) {
        console.log('Step 5: Testing API integration with axe results...');
        const testHtml = '<div><img src="test.jpg"><button>Click</button></div>';
        
        try {
          const apiResult = await VAE.API.fetchAAIInitialData({
            htmlContent: testHtml,
            pageUrl: window.location.href,
            axeResults: transformedAxeResults.slice(0, 3) // Send first 3 issues for testing
          });
          console.log('✓ API integration test successful:', apiResult);
        } catch (apiError) {
          console.warn('⚠ API integration test failed:', apiError);
        }
      } else {
        console.log('Step 5: Skipping API test (no token configured)');
      }

      console.log('=== Hybrid Test Complete ===');
      return {
        success: true,
        axeViolations: axeAnalysis.violations.length,
        transformedIssues: transformedAxeResults.length
      };

    } catch (error) {
      console.error('❌ Hybrid test failed:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Display axe results in console with nice formatting
  function displayAxeResults(axeResults) {
    if (axeResults.length === 0) {
      console.log('✓ No accessibility issues found by axe-core');
      return;
    }

    console.group('🔍 axe-core Issues Found:');
    
    const groupedByImpact = axeResults.reduce((groups, issue) => {
      const impact = issue.impact || 'unknown';
      if (!groups[impact]) groups[impact] = [];
      groups[impact].push(issue);
      return groups;
    }, {});

    Object.entries(groupedByImpact).forEach(([impact, issues]) => {
      console.group(`${getImpactIcon(impact)} ${impact.toUpperCase()} (${issues.length})`);
      
      issues.forEach(issue => {
        console.log(`📍 ${issue.description}`);
        console.log(`   Element: ${issue.selector}`);
        console.log(`   WCAG: ${issue.wcagCriterion}`);
        console.log(`   Auto-Fix: ${issue.isAutoFixable ? '✅ Yes' : '👨‍💻 Manual'} (Rule: ${issue.axeRuleId})`);
        if (issue.vaeId) {
          console.log(`   VAE ID: ${issue.vaeId}`);
        } else {
          console.log(`   VAE ID: ❌ Not found`);
        }
        if (issue.helpUrl) {
          console.log(`   Help: ${issue.helpUrl}`);
        }
      });
      
      console.groupEnd();
    });
    
    console.groupEnd();
  }

  // Get icon for impact level
  function getImpactIcon(impact) {
    const icons = {
      'critical': '🚨',
      'high': '⚠️',
      'medium': '🔶',
      'low': '💡',
      'unknown': '❓'
    };
    return icons[impact] || '❓';
  }

  // Calculate VAE ID coverage statistics
  function calculateVaeIdCoverage(axeResults) {
    const totalIssues = axeResults.length;
    const issuesWithVaeId = axeResults.filter(issue => issue.vaeId).length;
    const issuesWithoutVaeId = totalIssues - issuesWithVaeId;
    
    const coverage = totalIssues > 0 ? Math.round((issuesWithVaeId / totalIssues) * 100) : 0;
    
    return {
      total: totalIssues,
      withVaeId: issuesWithVaeId,
      withoutVaeId: issuesWithoutVaeId,
      coveragePercent: coverage,
      status: coverage >= 90 ? '✅ Excellent' : 
              coverage >= 70 ? '⚠️ Good' : 
              coverage >= 50 ? '🔶 Fair' : '❌ Poor'
    };
  }

  // Calculate auto-fixability statistics
  function calculateAutoFixabilityStats(axeResults) {
    const totalIssues = axeResults.length;
    const autoFixableIssues = axeResults.filter(issue => issue.isAutoFixable).length;
    const manualFixIssues = totalIssues - autoFixableIssues;
    
    const autoFixPercent = totalIssues > 0 ? Math.round((autoFixableIssues / totalIssues) * 100) : 0;
    
    return {
      total: totalIssues,
      autoFixable: autoFixableIssues,
      manualFix: manualFixIssues,
      autoFixPercent: autoFixPercent,
      status: autoFixPercent >= 70 ? '🚀 High Automation' : 
              autoFixPercent >= 50 ? '⚡ Good Automation' : 
              autoFixPercent >= 30 ? '🔧 Some Automation' : '👨‍💻 Mostly Manual'
    };
  }

  // Create test button in UI (only in development)
  function createTestButton() {
    // Only show test button in development environments
    if (window.location.hostname !== 'localhost' && 
        window.location.hostname !== '127.0.0.1' &&
        !window.location.search.includes('vae-debug=true')) {
      return; // Don't create button in production
    }

    if (document.getElementById('vae-hybrid-test-button')) {
      return; // Button already exists
    }

    const button = document.createElement('button');
    button.id = 'vae-hybrid-test-button';
    button.textContent = 'Test Hybrid Analysis';
    button.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 10000;
      padding: 10px 15px;
      background: #007cba;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-family: Arial, sans-serif;
      font-size: 12px;
    `;

    button.addEventListener('click', async () => {
      button.disabled = true;
      button.textContent = 'Testing...';
      
      const result = await runHybridTest();
      
      button.disabled = false;
      button.textContent = result.success ? '✓ Test Complete' : '❌ Test Failed';
      
      setTimeout(() => {
        button.textContent = 'Test Hybrid Analysis';
      }, 3000);
    });

    document.body.appendChild(button);
  }

  // Auto-run test if in development mode
  function autoRunIfDev() {
    if (window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1' ||
        window.location.search.includes('vae-test=hybrid')) {
      
      console.log('🧪 Development mode detected - auto-running hybrid test in 2 seconds...');
      setTimeout(runHybridTest, 2000);
    }
  }

  // Public API
  return {
    runHybridTest,
    createTestButton,
    autoRunIfDev
  };
})();

// Auto-initialize test features (only in development)
document.addEventListener('DOMContentLoaded', () => {
  if (window.VAE?.HybridTest) {
    // Only create test button in development environments
    VAE.HybridTest.createTestButton();
    // VAE.HybridTest.autoRunIfDev(); // Auskommentiert: Automatische Ausführung deaktiviert
  }
}); 